<template>
    <div class="voice-agent-page-content">
        <div class="header-section">
            <div class="title-wrapper">
                <h1 class="main-title">Ses Ajanları</h1>
                <p class="title-description">Bu sayfada ses ajanlarını yönetebilirsiniz</p>
            </div>
        </div>
        <div class="section">
            <div class="table-wrapper">
                <div class="toolbar">
                    <div class="left">
                        <a-input-search
                            style="width: 300px; margin: unset !important"
                            v-model="searchText"
                            placeholder="Ara"
                            class="search-input"
                            @input="onSearch"
                        />
                    </div>
                    <div class="right">
                        <PermissionController :permissions="['voice-agent.create']">
                            <SupsisButton
                                class="ai-suggest-button"
                                @click="$feature.beforeCreateVoiceAgent(openAIAssistantModal, agents.length)"
                                ghost
                                style="margin-right: 8px"
                            >
                                <img src="/static/img/copilot.png" class="copilot-icon" />
                                Supsis Asistan ile Ses Ajanı Oluştur
                            </SupsisButton>
                            <SupsisButton
                                primary
                                @click="$feature.beforeCreateVoiceAgent(openCreateAgentModal, agents.length)"
                            >
                                <i class="material-icons">add</i>
                                Yeni Ses Ajanı
                            </SupsisButton>
                        </PermissionController>
                    </div>
                </div>

                <a-spin :spinning="loading">
                    <SupsisTable
                        :columns="getCols()"
                        :rows="paginatedAgents"
                        :top-pagination="true"
                        empty-data-bordered
                        :bottom-pagination="false"
                        :pagination-cursor="pagination"
                        :options="{
                            rowStyleOption,
                            clipboardOption,
                            fixedHeader: true,
                            id: 'agents-table',
                            borderX: true,
                            borderY: true,
                        }"
                        @cursor-changed="(cursor) => (pagination = cursor)"
                    />
                </a-spin>
            </div>
        </div>

        <!-- Yeni Ajan Oluşturma Modal -->
        <Modal2
            v-if="showCreateModal"
            title="Yeni Ses Ajanı Oluştur"
            :okText="'Oluştur'"
            :cancelText="'İptal'"
            @ok="createAgent"
            @cancel="showCreateModal = false"
            xlarge
        >
            <div class="agent-form">
                <div class="form-item">
                    <label>Ajan Adı</label>
                    <a-input v-model="newAgent.agent_name" placeholder="Ajan adını girin" />
                </div>
                <div class="form-item">
                    <label>Ses Modeli</label>
                    <a-select v-model="newAgent.voice_model" style="width: 100%">
                        <a-select-option v-for="model in voiceModels" :key="model.id" :value="model.id">
                            {{ model.name }} - {{ model.description }}
                        </a-select-option>
                    </a-select>
                </div>
                <div class="form-item scenario-selector">
                    <label>Hazır Senaryo</label>
                    <div class="scenario-options">
                        <div
                            v-for="(scenario, index) in scenarioOptions"
                            :key="index"
                            :class="['scenario-card', { active: selectedScenario === index }]"
                            @click="selectScenario(index)"
                            :style="{ borderLeftColor: scenario.color, borderLeftWidth: '5px' }"
                        >
                            <div class="scenario-icon">
                                <i class="material-icons" :style="{ color: scenario.color }">{{ scenario.icon }}</i>
                            </div>
                            <div class="scenario-content">
                                <div class="scenario-title">{{ scenario.title }}</div>
                                <div class="scenario-description">{{ scenario.description }}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="selectedScenario === 3" class="form-item">
                    <label>Özel Senaryo Promptu</label>
                    <a-textarea
                        v-model="customPrompt"
                        placeholder="Ajanınızın prompt içeriğini buraya yazın..."
                        :rows="10"
                    ></a-textarea>
                    <small style="color: #888; margin-top: 4px; display: block">
                        Ajanınıza kimliğini, görevlerini ve konuşma tarzını tanımlayan bilgileri yazın
                    </small>
                </div>
            </div>
        </Modal2>

        <!-- AI Asistan Modal -->
        <Modal2
            v-if="showAIAssistantModal"
            title="AI Asistan ile Ses Ajanı Oluştur"
            :okText="'Prompt Oluştur'"
            :cancelText="'İptal'"
            @ok="generateAgentPrompt"
            @cancel="showAIAssistantModal = false"
        >
            <div class="ai-assistant-form">
                <p>
                    Oluşturmak istediğiniz ses ajanının hangi görevleri yapmasını istediğinizi detaylı bir şekilde
                    açıklayın. AI asistan, açıklamanıza uygun bir sesli asistan promptu ve ismi oluşturacaktır.
                </p>

                <div class="form-item">
                    <label>Ses Ajanı için İsteğiniz</label>
                    <S2Textarea
                        v-model="aiPromptRequest"
                        placeholder="Oluşturmak istediğiniz ses ajanını detaylıca anlatın. Örneğin: Müşteri hizmetleri için bir ses ajanı oluşturmak istiyorum. Bu ajan müşteri şikayetlerini almalı, müşteri bilgilerini doğrulamalı ve uygun departmana yönlendirmelidir..."
                        :rows="8"
                    ></S2Textarea>
                </div>

                <div v-if="isGeneratingPrompt" class="generating-prompt">
                    <a-spin />
                    <p>Ses ajanı promptu ve ismi oluşturuluyor...</p>
                </div>

                <div v-if="generatedAgentData" class="generated-prompt-container">
                    <div class="form-item">
                        <div class="section-header">
                            <h3>Oluşturulan Ajan İsmi</h3>
                            <a-button type="link" @click="createAgentFromGenerated">
                                <i class="material-icons">add</i> Bu Ajanı Oluştur
                            </a-button>
                        </div>
                        <a-input v-model="generatedAgentData.agent_name" disabled />
                    </div>

                    <div class="form-item">
                        <div class="section-header">
                            <h3>Ajan Açıklaması</h3>
                        </div>
                        <a-input v-model="generatedAgentData.description" disabled />
                    </div>

                    <div class="form-item">
                        <div class="section-header">
                            <h3>Oluşturulan Prompt</h3>
                            <a-button type="link" @click="copyPromptToClipboard">
                                <i class="material-icons">content_copy</i> Kopyala
                            </a-button>
                        </div>
                        <a-textarea v-model="generatedAgentData.prompt" :rows="10" readonly class="generated-prompt" />
                    </div>
                </div>
            </div>
        </Modal2>
    </div>
</template>

<script>
import { VOICE_AGENT_API } from "../../../../../../api/voiceAgent";
import SupsisTable from "../../../../../../components/SupsisTable.vue";
import SupsisButton from "../../../../Settings/components/SupsisButton.vue";
import Modal2 from "../../../../../../components/common/Modal2.vue";
import S2Textarea from "../../../../../../components/S2Textarea.vue";
import _ from "lodash";
import agentScenarios from "../../../voice-agent/data/agent-scenarios.json";
import CoPilot from "../../../../../../utils/CoPilot";


export default {
    name: "VoiceAgentAgents",
    components: {
        SupsisTable,
        SupsisButton,
        Modal2,
        S2Textarea,
    },
    props: {
        channelId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            searchText: "",
            agents: [],
            pagination: {
                page: 1,
                perPage: 10,
                total: 0,
            },
            loading: false,
            rowStyleOption: {
                stripe: true,
            },
            clipboardOption: {
                copy: true,
                paste: false,
                cut: false,
                delete: false,
            },
            showCreateModal: false,
            selectedScenario: 0,
            customPrompt: "",
            newAgent: {
                agent_name: "",
                voice_id: "11labs-Adrian",
                voice_model: "eleven_multilingual_v2",
                language: "tr-TR",
                response_engine: {
                    type: "retell-llm",
                },
            },
            voiceModels: [
                {
                    id: "eleven_multilingual_v2",
                    name: "Elevenlabs Multilingual v2",
                    description: "Multilingual, slow, highest quality",
                },
                {
                    id: "eleven_turbo_v2_5",
                    name: "Elevenlabs Turbo V2.5",
                    description: "Multilingual, fast, high quality",
                },
                {
                    id: "eleven_flash_v2_5",
                    name: "Elevenlabs Flash V2.5",
                    description: "Multilingual, fastest, medium quality",
                },
            ],
            scenarioOptions: agentScenarios,
            // AI Asistan ile ilgili state'ler
            showAIAssistantModal: false,
            aiPromptRequest: "",
            isGeneratingPrompt: false,
            generatedAgentData: null,
        };
    },
    computed: {
        filteredAgents() {
            if (!this.searchText) {
                return this.agents;
            }
            const searchLower = this.searchText.toLowerCase();
            return this.agents.filter((agent) => {
                const name = _.get(agent, "agent_name", "").toLowerCase();
                const type = _.get(agent, "response_engine.type", "").toLowerCase();
                return name.includes(searchLower) || type.includes(searchLower);
            });
        },
        paginatedAgents() {
            const start = (this.pagination.page - 1) * this.pagination.perPage;
            const end = start + this.pagination.perPage;
            return this.filteredAgents.slice(start, end);
        },
        totalPages() {
            return Math.ceil(this.filteredAgents.length / this.pagination.perPage);
        },
    },
    methods: {
        async fetchAgents() {
            try {
                this.loading = true;
                const agents = await VOICE_AGENT_API.agents.list();
                this.agents = agents;
                this.pagination.total = agents.length;
            } catch (error) {
                console.error("Ajanlar çekilirken hata oluştu:", error);
                this.$message.error("Ajanlar yüklenirken bir hata oluştu");
            } finally {
                this.loading = false;
            }
        },
        onSearch() {
            this.pagination.page = 1;
        },
        getCols() {
            return [
                {
                    key: "name",
                    field: "agent_name",
                    title: "İsim",
                    width: "22%",
                    renderBodyCell: ({ row }) => {
                        return (
                            <a class="agent-name-link" onClick={() => this.viewAgent(row.agent_id)}>
                                {_.get(row, "agent_name", "İsimsiz Ajan")}
                            </a>
                        );
                    },
                },
                {
                    key: "type",
                    field: "response_engine.type",
                    title: "Türü",
                    width: "13%",
                    renderBodyCell: ({ row }) => {
                        return _.get(row, "response_engine.type", "-").replace("retell-", "supsis-");
                    },
                },
                {
                    key: "agentType",
                    field: "agent_type",
                    title: "Ajan Tipi",
                    width: "15%",
                    renderBodyCell: ({ row }) => {
                        const states = _.get(row, "llm.states");
                        return Array.isArray(states) ? "Multi Prompt" : "Single Prompt";
                    },
                },
                {
                    key: "voice",
                    field: "voice_id",
                    title: "Ses",
                    width: "15%",
                    renderBodyCell: ({ row }) => {
                        return _.get(row, "voice.voice_name", "-");
                    },
                },
                {
                    key: "phone",
                    field: "phone",
                    title: "Telefon",
                    width: "15%",
                    renderBodyCell: ({ row }) => {
                        const phoneText = (_.get(row, "numbers", []) || [])
                            .map((number) => `${number.nickname}`)
                            .join(", ");
                        return (
                            <a-tooltip title={phoneText} placement="top">
                                <span class="phone-cell-text">{phoneText || "-"}</span>
                            </a-tooltip>
                        );
                    },
                },
                {
                    key: "editedBy",
                    field: "updatedBy",
                    title: "Düzenleyen",
                    width: "13%",
                    renderBodyCell: ({ row }) => {
                        return _.get(row, "updatedBy", "-");
                    },
                },
                {
                    key: "actions",
                    field: "actions",
                    title: "İşlemler",
                    width: "7%",
                    renderBodyCell: ({ row }) => {
                        return (
                            <div class="actions">
                                <PermissionController permissions={["voice-agent.update"]}>
                                    <SupsisButton
                                        class="edit-btn"
                                        onClick={() => this.editAgent(row.agent_id)}
                                        ghost
                                        icon="edit"
                                        small
                                    />
                                </PermissionController>
                                <SupsisButton
                                    class="view-btn"
                                    onClick={() => this.viewAgent(row.agent_id)}
                                    ghost
                                    icon="visibility"
                                    small
                                />
                                <PermissionController permissions={["voice-agent.delete"]}>
                                    <SupsisButton
                                        onClick={() => this.deleteAgent(row.agent_id)}
                                        danger
                                        ghost
                                        icon="delete"
                                        small
                                    />
                                </PermissionController>
                            </div>
                        );
                    },
                },
            ];
        },
        openCreateAgentModal() {
            // Formu sıfırla
            this.newAgent = {
                agent_name: "",
                voice_id: "11labs-Adrian",
                voice_model: "eleven_multilingual_v2",
                language: "tr-TR",
                response_engine: {
                    type: "retell-llm",
                },
            };
            this.selectedScenario = 0;
            this.customPrompt = "";
            this.showCreateModal = true;
        },
        selectScenario(index) {
            this.selectedScenario = index;
        },
        async createAgent() {
            if (!this.newAgent.agent_name) {
                this.$message.error("Lütfen ajan adını giriniz");
                return;
            }

            // Özel senaryo seçildiyse ve prompt boşsa uyarı göster
            if (this.selectedScenario === 3 && !this.customPrompt) {
                this.$message.error("Lütfen özel senaryo için prompt giriniz");
                return;
            }

            try {
                this.loading = true;

                // Seçilen senaryoyu al
                let selectedPrompt = this.scenarioOptions[this.selectedScenario].prompt;

                // Eğer özel senaryo seçildiyse, kullanıcının girdiği promptu kullan
                if (this.selectedScenario === 3) {
                    selectedPrompt = this.customPrompt;
                }

                // Önce LLM oluştur
                const llmData = {
                    model: "gpt-4o-mini",
                    model_temperature: 0.7,
                    general_prompt: selectedPrompt,
                };

                const createdLLM = await VOICE_AGENT_API.llms.create(llmData);

                // LLM ID'yi kullanarak ajanı oluştur
                const agentData = {
                    ...this.newAgent,
                    response_engine: {
                        type: "retell-llm",
                        llm_id: createdLLM.llm_id,
                    },
                };

                const createdAgent = await VOICE_AGENT_API.agents.create(agentData);
                this.$message.success("Ses ajanı başarıyla oluşturuldu");
                this.showCreateModal = false;

                // Ajanı listeye ekle
                await this.fetchAgents();

                // Yeni oluşturulan ajanın detay sayfasına git
                this.viewAgent(createdAgent.agent_id);
            } catch (error) {
                console.error("Ajan oluşturulurken hata oluştu:", error);
                this.$message.error("Ajan oluşturulurken bir hata oluştu");
            } finally {
                this.loading = false;
            }
        },
        editAgent(agentId) {
            // Ajan düzenleme işlemi
            this.$router.push({
                path: this.$route.path,
                query: {
                    agent_id: agentId,
                    editor_tab: "agent",
                },
            });
        },
        viewAgent(agentId) {
            // Ajan detayı görüntüleme işlemi
            this.$router.push({
                path: this.$route.path,
                query: {
                    agent_id: agentId,
                    editor_tab: "agent",
                },
            });
        },
        async deleteAgent(agentId) {
            try {
                await this.$confirm({
                    title: "Ses Ajanını Sil",
                    content: "Bu ses ajanını silmek istediğinize emin misiniz?",
                    okText: "Evet",
                    okType: "danger",
                    cancelText: "Hayır",
                    onOk: async () => {
                        this.loading = true;
                        await VOICE_AGENT_API.agents.delete(agentId);
                        this.agents = this.agents.filter((agent) => agent._id !== agentId);
                        this.pagination.total = this.agents.length;
                        this.loading = false;
                        this.$message.success("Ses ajanı başarıyla silindi");
                    },
                });
            } catch (error) {
                console.error("Ajan silinirken hata oluştu:", error);
                this.$message.error("Ajan silinirken bir hata oluştu");
            }
        },
        // AI Asistan ile ilgili metodlar
        openAIAssistantModal() {
            this.showAIAssistantModal = true;
            this.aiPromptRequest = "";
            this.generatedAgentData = null;
        },

        async generateAgentPrompt() {
            if (!this.aiPromptRequest.trim()) {
                this.$message.error("Lütfen ses ajanı için bir istek girin");
                return;
            }

            try {
                this.isGeneratingPrompt = true;

                // CoPilot yardımcı sınıfını kullanarak prompt oluştur
                const copilot = new CoPilot.getInstance();

                // Copilot'a isteği gönder
                const siteId = this.$store.state.site.site._id;
                const userId = this.$store.state.auth.user._id;

                // voice-agent-prompt.md dosyasındaki promptu kullanarak istek gönder
                const response = await copilot.generateVoiceResponse(this.aiPromptRequest);

                try {
                    // Response JSON olarak parse et
                    const parsedResponse = JSON.parse(response);
                    console.log("[generateVoiceResponse] parsedResponse", parsedResponse);
                    this.generatedAgentData = parsedResponse;

                    // Eğer response doğru formatta değilse hata ver
                    if (!parsedResponse.agent_name || !parsedResponse.prompt) {
                        throw new Error("Geçersiz yanıt formatı");
                    }
                } catch (parseError) {
                    console.error("AI yanıtı parse edilemedi:", parseError);
                    this.$message.error("AI yanıtı doğru formatta değil, lütfen tekrar deneyin");
                }
            } catch (error) {
                console.error("Prompt oluşturma hatası:", error);
                this.$message.error("Prompt oluşturulurken bir hata oluştu");
            } finally {
                this.isGeneratingPrompt = false;
            }
        },

        copyPromptToClipboard() {
            if (this.generatedAgentData && this.generatedAgentData.prompt) {
                navigator.clipboard
                    .writeText(this.generatedAgentData.prompt)
                    .then(() => {
                        this.$message.success("Prompt panoya kopyalandı");
                    })
                    .catch((err) => {
                        console.error("Kopyalama hatası:", err);
                        this.$message.error("Prompt kopyalanırken bir hata oluştu");
                    });
            }
        },

        createAgentFromGenerated() {
            if (!this.generatedAgentData) {
                return;
            }

            // Oluşturulan ajanı kullanarak yeni bir ajan oluştur modülünü aç
            this.showCreateModal = true;
            this.showAIAssistantModal = false;

            // Form'u doldur
            this.newAgent.agent_name = this.generatedAgentData.agent_name;

            // Özel senaryo seç
            this.selectedScenario = 3; // Özel senaryo indeksi
            this.customPrompt = this.generatedAgentData.prompt;
        },
    },
    created() {
        this.fetchAgents();
    },
    watch: {
        pagination: {
            handler() {
                // Sayfa değişikliğinde yapılacak işlemler
            },
            deep: true,
        },
    },
};
</script>

<style scoped lang="scss">
.voice-agent-page-content {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 0 20px;

    .header-section {
        margin-bottom: 5px;
    }

    .title-wrapper {
        display: flex;
        align-items: center;
        gap: 15px;
        margin-bottom: 20px;
    }

    .main-title {
        font-size: 24px;
        font-weight: 500;
        margin: 0;
    }

    .title-description {
        font-size: 14px;
        font-weight: 400;
        color: #8c8c8c;
        margin: 0;
    }

    .section {
        display: flex;
        flex-direction: column;
        gap: 15px;
    }

    .table-wrapper {
        display: flex;
        flex-direction: column;
        gap: 10px;
        overflow-x: hidden;

        .toolbar {
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 10px 0;
            gap: 5px;

            .left {
                display: flex;
                gap: 5px;
                align-items: center;
            }

            .right {
                display: flex;
                gap: 5px;

                .ai-suggest-button {
                    display: flex;
                    align-items: center;
                    //background-color: transparent;
                    //border: none;
                    box-shadow: none;
                    color: rgba(0, 0, 0, 0.65);
                    padding: 8px 12px;
                    border-radius: 4px;
                    transition: all 0.3s;

                    &:hover {
                        background-color: #f0f5ff;
                        color: #1890ff;
                    }

                    i {
                        font-size: 18px;
                        margin-right: 8px;
                    }

                    span {
                        font-size: 14px;
                    }
                }

                .copilot-icon {
                    vertical-align: middle;
                    margin-right: 5px;
                    width: 15px;
                    height: 15px;
                }
            }
        }
    }

    #agents-table {
        .actions {
            display: flex;
            gap: 5px;
        }

        .agent-name-link {
            color: inherit;
            text-decoration: none;
            cursor: pointer;

            &:hover {
                text-decoration: underline !important;
            }
        }

        .phone-cell-text {
            display: block;
            max-width: 100%;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            cursor: default;
        }
    }
}
</style>
<style lang="scss">
.agent-form {
    display: flex;
    flex-direction: column;
    gap: 15px;

    .form-item {
        display: flex;
        flex-direction: column;
        gap: 5px;

        label {
            font-weight: 500;
        }
    }

    .scenario-selector {
        margin-top: 10px;
    }

    .scenario-options {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
        margin-top: 8px;
    }

    .scenario-card {
        border: 1px solid #e8e8e8;
        border-radius: 8px;
        padding: 12px;
        cursor: pointer;
        transition: all 0.3s;
        height: 100%;
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 12px;
        overflow: hidden;
        position: relative;
    }

    .scenario-card:hover {
        border-color: #1890ff;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.09);
        transform: translateY(-2px);
    }

    .scenario-card.active {
        border-color: #1890ff;
        background-color: #e6f7ff;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.09);
    }

    .scenario-icon {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .scenario-icon .material-icons {
        font-size: 24px;
    }

    .scenario-content {
        flex: 1;
    }

    .scenario-title {
        font-weight: 500;
        margin-bottom: 5px;
        font-size: 14px;
        color: #333;
    }

    .scenario-description {
        font-size: 12px;
        color: #666;
    }
}

.ai-assistant-form {
    display: flex;
    flex-direction: column;
    gap: 20px;

    p {
        margin: 0;
        color: #666;
    }

    .form-item {
        display: flex;
        flex-direction: column;
        gap: 5px;

        label {
            font-weight: 500;
        }
    }

    .generating-prompt {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 10px;
        padding: 20px;
        background-color: #f5f5f5;
        border-radius: 4px;

        p {
            margin: 0;
        }
    }

    .generated-prompt-container {
        display: flex;
        flex-direction: column;
        gap: 15px;
        padding: 15px;
        background-color: #f9f9f9;
        border-radius: 4px;
        border: 1px solid #e8e8e8;

        .section-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 5px;

            h3 {
                margin: 0;
                font-size: 16px;
                color: #333;
            }
        }

        .generated-prompt {
            font-family: "Courier New", Courier, monospace;
            background-color: #f5f5f5;
        }
    }
}
</style>
